import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSyncStatus } from '../hooks/useSyncStatus';
import offlineEngine from '../services/offlineEngine';
import './SyncChip.css';

/* ── SVG icons (inline, no external deps) ──────────────────────────────── */

function CloudIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
    </svg>
  );
}

function CloudCheckIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
      <path d="M9 15l2 2 4-4" />
    </svg>
  );
}

function CloudOffIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22.61 16.95A5 5 0 0018 10h-1.26a8 8 0 00-7.05-6M5 5a8 8 0 004 15h9a5 5 0 001.7-.3" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );
}

function CloudXIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 10h-1.26A8 8 0 109 20h9a5 5 0 000-10z" />
      <line x1="10" y1="13" x2="14" y2="17" />
      <line x1="14" y1="13" x2="10" y2="17" />
    </svg>
  );
}

/* ── Derive chip state ─────────────────────────────────────────────────── */

function deriveState(status) {
  const { mode, online, pending, failed, syncing } = status;
  if (mode === 'offline' || !online) return 'offline';
  if (failed > 0) return 'error';
  if (pending > 0 || syncing) return 'pending';
  return 'synced';
}

/* ── SyncChip ──────────────────────────────────────────────────────────── */

export default function SyncChip() {
  const status = useSyncStatus();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const longPressTimer = useRef(null);
  const didLongPress = useRef(false);

  const state = deriveState(status);

  function handleTap() {
    if (didLongPress.current) { didLongPress.current = false; return; }
    if (status.mode === 'offline') {
      offlineEngine.setMode('auto');
    } else {
      offlineEngine.setMode('offline');
    }
  }

  const startLongPress = useCallback(() => {
    didLongPress.current = false;
    longPressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      setDrawerOpen(true);
    }, 600);
  }, []);

  const cancelLongPress = useCallback(() => {
    if (longPressTimer.current) clearTimeout(longPressTimer.current);
  }, []);

  function handleClearFailed() {
    offlineEngine.clearFailed();
  }

  const icon = state === 'offline' ? <CloudOffIcon />
    : state === 'error' ? <CloudXIcon />
    : state === 'synced' ? <CloudCheckIcon />
    : <CloudIcon />;

  const label = state === 'offline' ? 'Offline'
    : state === 'error' ? 'Error'
    : state === 'pending' ? String(status.pending + status.failed)
    : 'Synced';

  return (
    <>
      <button
        className={`sync-chip sync-chip--${state}`}
        onClick={handleTap}
        onMouseDown={startLongPress}
        onMouseUp={cancelLongPress}
        onMouseLeave={cancelLongPress}
        onTouchStart={startLongPress}
        onTouchEnd={cancelLongPress}
        onTouchCancel={cancelLongPress}
        type="button"
        aria-label={`Sync status: ${label}`}
      >
        <span className="sync-chip__icon">{icon}</span>
        <span className="sync-chip__label">{label}</span>
      </button>

      {drawerOpen && createPortal(
        <div className="sync-drawer-backdrop" onClick={() => setDrawerOpen(false)}>
          <div className="sync-drawer" onClick={e => e.stopPropagation()}>
            <div className="sync-drawer__header">
              <span className="sync-drawer__title">Sync Queue</span>
              <button className="sync-drawer__close" onClick={() => setDrawerOpen(false)}>×</button>
            </div>
            <div className="sync-drawer__body">
              <div className="sync-drawer__row">
                <span>Pending</span>
                <span className="sync-drawer__count">{status.pending}</span>
              </div>
              <div className="sync-drawer__row">
                <span>Failed</span>
                <span className="sync-drawer__count sync-drawer__count--error">{status.failed}</span>
              </div>
              {status.failed > 0 && (
                <button className="sync-drawer__clear" onClick={handleClearFailed}>
                  Clear Failed
                </button>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
